import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Linkedin, Check, Loader2, Calendar, ArrowUpRight } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useSeo } from "../lib/seo";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAL_URL = process.env.REACT_APP_CAL_URL || "https://cal.com/wald";

const industriesOptions = [
  "Agricultural Exporter",
  "Packhouse",
  "Grower Organisation",
  "Export Agent",
  "Regulated Organisation",
  "Other",
];

export default function ContactPage() {
  useSeo({
    title: "Contact Wald — Agricultural Software Development, South Africa",
    description: "Book a discovery call or send an enquiry to Wald Enterprise, South Africa's agricultural technology partner for AgriTech, farm management software and agricultural ERP.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", industry: "", message: "", consent: true,
  });
  const [status, setStatus] = useState("idle");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("success");
      toast.success("Enquiry sent. We will respond within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", industry: "", message: "", consent: true });
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };
  return (
    <SiteLayout>
      <Toaster position="top-right" richColors />
      <section className="border-b border-border">
        <div className="container-wald py-20 md:py-28">
          <div className="eyebrow mb-6">Contact</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            A 30-minute discovery call.
          </h1>
          <p className="mt-6 max-w-2xl opacity-80 leading-relaxed text-lg">
            We listen to what you are building or fixing and tell you honestly whether Wald is the right
            partner. No pitch deck.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={CAL_URL} target="_blank" rel="noreferrer noopener" data-testid="contact-cal-cta" className="btn-copper">
              <Calendar size={16} strokeWidth={2} /> Book on Cal.com <ArrowUpRight size={14} strokeWidth={2} />
            </a>
            <a href="#form" className="btn-outline">Send an enquiry instead</a>
          </div>
        </div>
      </section>
      <section id="form" className="border-b border-border">
        <div className="container-wald py-20">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-4 space-y-8">
              <div>
                <div className="eyebrow mb-3">Direct channels</div>
                <ul className="space-y-3 text-sm">
                  <li className="flex gap-3 items-center"><Mail size={16} strokeWidth={2} className="text-[color:var(--wald-copper)]" /> info@wald.co.za</li>
                  <li className="flex gap-3 items-center"><Phone size={16} strokeWidth={2} className="text-[color:var(--wald-copper)]" /> +27 (0)71 336 0548</li>
                  <li className="flex gap-3 items-center"><MapPin size={16} strokeWidth={2} className="text-[color:var(--wald-copper)]" /> Pretoria, South Africa</li>
                  <li className="flex gap-3 items-center"><Linkedin size={16} strokeWidth={2} className="text-[color:var(--wald-copper)]" /> LinkedIn</li>
                </ul>
              </div>
              <div className="border border-border p-5 bg-card">
                <div className="eyebrow mb-3">Prefer to just pick a time?</div>
                <p className="text-sm opacity-80 leading-relaxed mb-4">
                  30 minutes. Video or phone. No obligation.
                </p>
                <a href={CAL_URL} target="_blank" rel="noreferrer noopener" className="btn-outline text-xs w-full justify-center" data-testid="contact-cal-inline">
                  <Calendar size={13} /> Open Cal.com
                </a>
              </div>
              <div className="border border-border p-5">
                <div className="eyebrow mb-3">Response time</div>
                <p className="text-sm opacity-80 leading-relaxed">One business day. Personal reply, not an autoresponder.</p>
              </div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full name" required>
                    <input required data-testid="contact-input-name" value={form.name}
                      onChange={(e) => setField("name", e.target.value)} className="wald-input" />
                  </Field>
                  <Field label="Work email" required>
                    <input required type="email" data-testid="contact-input-email" value={form.email}
                      onChange={(e) => setField("email", e.target.value)} className="wald-input" />
                  </Field>
                  <Field label="Company">
                    <input data-testid="contact-input-company" value={form.company}
                      onChange={(e) => setField("company", e.target.value)} className="wald-input" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="contact-input-phone" value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)} className="wald-input" />
                  </Field>
                </div>
                <Field label="Industry">
                  <select data-testid="contact-input-industry" value={form.industry}
                    onChange={(e) => setField("industry", e.target.value)} className="wald-input">
                    <option value="">Select industry</option>
                    {industriesOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                  </select>
                </Field>
                <Field label="What are you exploring?" required>
                  <textarea required data-testid="contact-input-message" value={form.message}
                    onChange={(e) => setField("message", e.target.value)} rows={6}
                    className="wald-input resize-y"
                    placeholder="Share the operational problem, product idea, or current stack you want to talk about." />
                </Field>
                <label className="flex gap-3 items-start text-xs opacity-80">
                  <input type="checkbox" data-testid="contact-input-consent" checked={form.consent}
                    onChange={(e) => setField("consent", e.target.checked)}
                    className="mt-1 accent-[color:var(--wald-copper)]" />
                  <span>
                    I consent to Wald processing my details under POPIA to respond to this enquiry.
                  </span>
                </label>
                <div className="pt-2">
                  <button type="submit" data-testid="contact-submit" disabled={status === "submitting"}
                    className="btn-copper disabled:opacity-70">
                    {status === "submitting" ? (
                      <><Loader2 size={16} strokeWidth={2} className="animate-spin" /> Sending…</>
                    ) : status === "success" ? (
                      <><Check size={16} strokeWidth={2} /> Sent</>
                    ) : (
                      "Send enquiry"
                    )}
                  </button>
                </div>
              </form>
              <style>{`
                .wald-input {
                  width: 100%;
                  background: hsl(var(--background));
                  border: 1px solid hsl(var(--border));
                  color: hsl(var(--foreground));
                  padding: 0.7rem 0.9rem;
                  font-size: 0.9rem;
                  border-radius: 2px;
                  transition: border-color 200ms ease, box-shadow 200ms ease;
                  outline: none;
                }
                .wald-input:focus {
                  border-color: var(--wald-copper);
                  box-shadow: 0 0 0 3px rgba(214, 90, 28, 0.15);
                }
              `}</style>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="eyebrow mb-2">
      {label}{required && <span className="text-[color:var(--wald-copper)]"> *</span>}
    </div>
    {children}
  </label>
);
